/**
 * Validates a `returnUrl` from the query string, which is attacker-controlled, into a
 * router-relative path. Rejects anything off-site, including the protocol-relative "//host"
 * form and backslashes, which some browsers normalise into it.
 *
 * Dependency-free on purpose: the auth route guards use this while the router is still being
 * constructed.
 */
export function safeReturnUrl(returnUrl) {
  if (typeof returnUrl !== 'string' || !returnUrl) return '/'
  if (returnUrl[0] !== '/') return '/'
  if (returnUrl[1] === '/' || returnUrl[1] === '\\') return '/'
  if (returnUrl.indexOf('\\') !== -1) return '/'
  return returnUrl
}

/**
 * The returnUrl to carry into the login page for a route the user is being bounced off, or
 * undefined when there is nothing worth returning to.
 *
 * A route's fullPath is only a router-relative path when the browser was inside the SPA base.
 * Outside it, vue-router's getLocation cannot strip the base and reports window.location.pathname
 * verbatim - '/uniq' for a browser at http://host/uniq while the app is served from /uniq/v2 -
 * and no route matches. Carrying that through would prepend the base after sign-in and navigate
 * to /uniq/v2/uniq, which does not exist.
 *
 * @param {{ fullPath?: string, matched?: Array }} [route] a vue-router Route
 */
export function returnUrlFor(route) {
  if (!route || !route.matched || !route.matched.length) return undefined
  return route.fullPath
}

/** SPA base without a trailing slash, so '' / '/v2' / '/uniq/v2' all concatenate cleanly. */
function appBase() {
  return (process.env.BASE_URL || '/').replace(/\/+$/, '')
}

/**
 * A path for window.location, including the SPA base - the app is not served from the server
 * root, so a router-relative path is not navigable as-is.
 *
 * Idempotent, because returnUrl arrives in two shapes: router-relative from the Vue guard
 * ('/quote/listing') and server-absolute from SignIn.aspx ('/uniq/v2/quote/listing').
 */
export function resolveAppUrl(returnUrl) {
  const base = appBase()
  let path = safeReturnUrl(returnUrl)

  // Already base-qualified - strip it so it is not applied twice.
  if (base && (path === base || path.indexOf(base + '/') === 0)) {
    path = path.slice(base.length) || '/'
  }

  return (base + path) || '/'
}

export default { safeReturnUrl, returnUrlFor, resolveAppUrl }
